import { Linking } from 'react-native';
import { EnrichedText } from 'react-native-enriched-html';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import { isSafeRichTextLink, richTextSystemFontFamily } from './RichText.utils';

const underlineTextDecoration: 'underline' = 'underline';
type RichTextDisplaySize = 'md' | 'sm' | 'xs';
type RichTextDisplayVariant = 'typography' | 'typographySecondary';

const ThemedEnrichedText = withUnistyles(EnrichedText, (theme) => ({
  htmlStyle: {
    a: {
      color: theme.colors.primary,
      pressColor: theme.colors.primaryActive,
      textDecorationLine: underlineTextDecoration,
    },
    blockquote: {
      borderColor: theme.colors.borderSecondary,
      borderWidth: StyleSheet.hairlineWidth,
      color: theme.colors.typographySecondary,
      gapWidth: theme.space('sm'),
    },
    code: {
      backgroundColor: theme.colors.surfaceMuted,
      color: theme.colors.typography,
    },
    codeblock: {
      backgroundColor: theme.colors.surfaceMuted,
      borderRadius: theme.borderRadius.md,
      color: theme.colors.typography,
    },
    h1: { bold: true, fontSize: theme.fontSize['3xl'] },
    h2: { bold: true, fontSize: theme.fontSize['2xl'] },
    h3: { bold: true, fontSize: theme.fontSize.xl },
    h4: { bold: true, fontSize: theme.fontSize.lg },
    h5: { bold: true, fontSize: theme.fontSize.md },
    h6: { bold: true, fontSize: theme.fontSize.sm },
    ol: {
      markerColor: theme.colors.typography,
      markerFontWeight: theme.fontWeight.normal,
    },
    ul: {
      bulletColor: theme.colors.typography,
      bulletSize: theme.size(2),
    },
    ulCheckbox: {
      boxColor: theme.colors.primary,
      boxSize: theme.size(5),
      gapWidth: theme.space('sm'),
      marginLeft: theme.space('md'),
    },
  },
  selectionColor: theme.colors.primaryMuted,
}));

export default function RichTextDisplay({
  html,
  numberOfLines,
  size = 'md',
  variant = 'typography',
}: {
  html: string;
  numberOfLines?: number;
  size?: RichTextDisplaySize;
  variant?: RichTextDisplayVariant;
}) {
  return (
    <ThemedEnrichedText
      numberOfLines={numberOfLines}
      onLinkPress={({ url }) => {
        if (isSafeRichTextLink(url)) {
          void Linking.openURL(url);
        }
      }}
      selectable
      style={styles.text({ size, variant })}
      useHtmlNormalizer
    >
      {html}
    </ThemedEnrichedText>
  );
}

const styles = StyleSheet.create((theme) => ({
  text: ({
    size,
    variant,
  }: {
    size: RichTextDisplaySize;
    variant: RichTextDisplayVariant;
  }) => ({
    color:
      variant === 'typographySecondary'
        ? theme.colors.typographySecondary
        : theme.colors.typography,
    flexShrink: 1,
    fontFamily: richTextSystemFontFamily,
    fontSize: theme.fontSize[size],
    width: '100%',
  }),
}));
